// frontend/src/components/ImageViewer.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Chip,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Home as HomeIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import {
  getPresignedDownloadUrl,
  getFileMetadata,
  getFileName,
  apiLogger,
  axiosInstance,
} from '../services/api';
import { getDisplayFileName } from '../services/FileUtils';

const COMPONENT_CONTEXT = 'ImageViewer';

const ImageViewer: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const decodedKey = key ? decodeURIComponent(key) : '';
  const displayFileName = getDisplayFileName(decodedKey);
  const originalFileName = getFileName(decodedKey);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<any>(null);
  const [zoom, setZoom] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);

  // Fetch image via backend proxy to avoid S3 CORS
  const loadImage = useCallback(async () => {
    if (!decodedKey) {
      setError('No file specified');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const [imageResponse, downloadResponse] = await Promise.all([
        axiosInstance.get<ArrayBuffer>('/api/content', {
          params: { key: decodedKey },
          responseType: 'arraybuffer',
        }),
        getPresignedDownloadUrl(decodedKey),
      ]);

      const ext = decodedKey.split('.').pop()?.toLowerCase() || 'png';
      const mimeMap: Record<string, string> = {
        jpg: 'image/jpeg', jpeg: 'image/jpeg',
        png: 'image/png', gif: 'image/gif',
        webp: 'image/webp', bmp: 'image/bmp',
        svg: 'image/svg+xml',
      };
      const mimeType = mimeMap[ext] || 'image/png';
      const blob = new Blob([imageResponse.data], { type: mimeType });
      const objectUrl = URL.createObjectURL(blob);

      setImageSrc(objectUrl);
      setDownloadUrl(downloadResponse.url);

      apiLogger.info(COMPONENT_CONTEXT, `Image loaded: ${decodedKey}`);
    } catch (err) {
      apiLogger.error(COMPONENT_CONTEXT, `Failed to load image: ${err}`, err);
      setError('Failed to load image. The file may be unavailable or unsupported.');
    } finally {
      setLoading(false);
    }
  }, [decodedKey]);

  // Fetch metadata separately (non-blocking)
  useEffect(() => {
    loadImage();
    getFileMetadata(decodedKey)
      .then(r => setMetadata(r.metadata))
      .catch(() => {}); // non-critical
  }, [loadImage, decodedKey]);

  // Revoke blob URL on unmount to free memory
  useEffect(() => {
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [imageSrc]);

  const handleDownload = useCallback(async () => {
    if (!downloadUrl) return;
    try {
      setIsDownloading(true);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = originalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      apiLogger.error(COMPONENT_CONTEXT, `Download failed: ${err}`, err);
    } finally {
      setIsDownloading(false);
    }
  }, [downloadUrl, originalFileName]);

  const handleZoomIn = () => setZoom(z => Math.min(z + 0.25, 4));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.25, 0.25));
  const handleFitScreen = () => setZoom(1);

  return (
    <Box sx={{ my: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Typography color="text.primary">{displayFileName}</Typography>
      </Breadcrumbs>

      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2">
            {displayFileName}
          </Typography>
          <Chip icon={<ImageIcon />} label="IMAGE" color="primary" variant="outlined" size="small" />
        </Box>

        {decodedKey !== displayFileName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Path: {decodedKey}
          </Typography>
        )}

        {/* Metadata chips */}
        {metadata && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {metadata.contentLength && (
              <Chip size="small" label={`Size: ${(metadata.contentLength / 1024).toFixed(1)} KB`} variant="outlined" />
            )}
            {metadata.lastModified && (
              <Chip size="small" label={`Modified: ${new Date(metadata.lastModified).toLocaleDateString()}`} variant="outlined" />
            )}
          </Box>
        )}

        {/* Zoom controls */}
        {imageSrc && !loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Tooltip title="Zoom out">
              <IconButton onClick={handleZoomOut} disabled={zoom <= 0.25} size="small">
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Zoom in">
              <IconButton onClick={handleZoomIn} disabled={zoom >= 4} size="small">
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fit to screen">
              <IconButton onClick={handleFitScreen} size="small">
                <FitScreenIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Image display area */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        ) : imageSrc ? (
          <Box
            sx={{
              overflow: 'auto',
              maxHeight: '70vh',
              border: '1px solid rgba(0,0,0,0.1)',
              borderRadius: 1,
              backgroundColor: '#f0f0f0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              p: 2,
              mb: 3,
            }}
          >
            <img
              src={imageSrc}
              alt={displayFileName}
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
                maxWidth: zoom === 1 ? '100%' : 'none',
                transition: 'transform 0.2s ease',
                display: 'block',
              }}
            />
          </Box>
        ) : null}

        {/* Action buttons */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={loading || isDownloading || !downloadUrl}
          >
            {isDownloading ? <CircularProgress size={24} /> : 'Download'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back to List
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ImageViewer;
